/*
 * Copyright © 2017-2026 Knowledge for Decisions (K4D). All rights reserved.
 *
 * This code and associated materials are protected by copyright laws and
 * international treaties. Unauthorized use, reproduction, distribution, or
 * modification of any content from Knowledge for Decisions (K4D) is strictly
 * prohibited. For inquiries regarding the use or licensing of K4D's
 * copyrighted materials, please contact us at contact@k4d.com.br. "Knowledge
 * for Decisions (K4D)" and the slogan "Using data to make precise decisions"
 * are registered trademarks of Knowledge for Decisions (K4D).
 */

import React from 'react';
import { Button, Text, Stack, Group, Anchor } from '@/shared/ui';
import { useTranslation, Trans } from 'react-i18next';
import { useCduConsent } from '../hooks/useCduConsent';

/**
 * CDU Consent Banner Component.
 * Displays a fixed banner at the bottom-right corner asking for user consent
 * for cookies and data usage. Shows when:
 * - Consent status is 'pending' (initial state)
 * - Consent is 'rejected' and user tries to login (showBannerForLogin is true)
 *
 * @returns {JSX.Element | null} The consent banner or null if consent is already given
 */
export const CduConsentBanner: React.FC = () => {
  const { t } = useTranslation('features/cookie-consent');
  const { consentStatus, acceptConsent, rejectConsent, showBannerForLogin, clearLoginBanner } = useCduConsent();

  // Don't render if consent is already given and not showing for login
  if (consentStatus === 'accepted' && !showBannerForLogin) {
    return null;
  }

  // Show banner if pending or if rejected and login was attempted
  const shouldShow = consentStatus === 'pending' || (consentStatus === 'rejected' && showBannerForLogin);
  
  if (!shouldShow) {
    return null;
  }

  const handleAccept = async () => {
    await acceptConsent();
    clearLoginBanner();
  };

  const handleReject = async () => {
    await rejectConsent();
    clearLoginBanner();
  };

  return (
    <div
      className="fixed left-0 right-0 z-50"
      role="region"
      aria-label={t('banner.title')}
      style={{
        bottom: 'var(--bottom-nav-height, 0px)',
        backgroundColor: 'var(--surface-primary)',
        borderTop: '1px solid var(--border-subtle)',
        padding: 'var(--space-6)',
        boxShadow: 'var(--shadow-md)',
      }}
    >
        <Stack gap="md">
          <Text className="text-(--text-primary)" fw={600} size="lg">
            {t('banner.title')}
          </Text>

          <Text className="leading-relaxed" size="sm" style={{ color: 'var(--text-secondary)' }}>
            {showBannerForLogin && consentStatus === 'rejected' 
              ? (
                <Trans
                  i18nKey="banner.loginRequiredMessage"
                  ns="features/cookie-consent"
                  components={{
                    policyLink: (
                      <Anchor
                        href="/cookies/latest"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="underline font-medium"
                      />
                    ),
                  }}
                />
              )
              : (
                <>
                  {t('banner.message')}{' '}
                  <Anchor
                    href="/cookies/latest"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="underline font-medium"
                  >
                    {t('banner.cookiePolicyLink')}
                  </Anchor>
                  .
                </>
              )
            }
          </Text>

          <Group className="justify-end" gap="sm">
            <Button
              variant="outline"
              size="sm"
              onClick={handleReject}
              aria-label={t('banner.rejectButton')}
            >
              {t('banner.rejectButton')}
            </Button>
            <Button
              size="sm"
              onClick={handleAccept}
              aria-label={t('banner.acceptButton')}
            >
              {t('banner.acceptButton')}
            </Button>
          </Group>
        </Stack>
    </div>
  );
};

export default CduConsentBanner;
